import { Link, useNavigate } from "@tanstack/react-router"
import { Button } from "@/components/ui/button"
import { useApiQuery } from "@/api/ApiQueryServiceProvider"
import { buildRoleUri } from "@/hooks/useRemaUriBuilder"
import { ArrowLeft, Loader2, Edit, ShieldCheck, Link2 } from "lucide-react"
import { ProfileHeader, ProfileSection } from "@/components/sensnet/ProfileLayout"

interface RoleDetailPageProps {
  roleNumber: number | string
}

export function RoleDetailPage({ roleNumber }: RoleDetailPageProps) {
  const navigate = useNavigate()

  const { data, isLoading, isError } = useApiQuery("getRoles", {})
  const role = ((data as any)?.content?.roles || (data as any)?.roles || []).find(
    (r: any) => String(r.roleNumber) === String(roleNumber)
  )

  const uri = role ? buildRoleUri({
    namespace: role.namespace ?? '',
    roleType: role.roleType ?? '',
    organization: role.organization ?? '',
    roleName: role.roleName ?? '',
  }) : ''

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="flex items-center gap-2">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <span className="text-muted-foreground">Loading role details...</span>
        </div>
      </div>
    )
  }

  if (isError || !role) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <p className="text-sm text-muted-foreground">Role not found.</p>
        <Button variant="outline" onClick={() => navigate({ to: '/rema/roles' })}>Back to list</Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button asChild variant="ghost" size="sm" className="gap-2 -ml-2">
          <Link to="/rema/roles">
            <ArrowLeft className="h-4 w-4" />
            Back
          </Link>
        </Button>
        <Button asChild size="sm" className="gap-2">
          <Link to="/rema/roles/$roleNumber/edit" params={{ roleNumber: String(role.roleNumber) }}>
            <Edit className="h-4 w-4" />
            Edit
          </Link>
        </Button>
      </div>

      <ProfileHeader
        title={role.roleName}
        subtitle={`Role Number: ${role.roleNumber}`}
        subtitleIcon={<ShieldCheck className="h-4 w-4" />}
        badge={role.roleType}
      />

      <ProfileSection title="Identity" icon={<ShieldCheck className="h-4 w-4" />} variant="dark">
        <div className="space-y-3 p-5">
          <div>
            <p className="text-xs text-muted-foreground">Name</p>
            <p className="text-sm">{role.roleName}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Type</p>
            <p className="text-sm">{role.roleType}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Organization</p>
            <p className="text-sm">{role.organization}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Namespace</p>
            <p className="text-sm font-mono">{role.namespace}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Description</p>
            <p className="text-sm">{role.roleDescription || '—'}</p>
          </div>
        </div>
      </ProfileSection>

      <ProfileSection title="Role URI" icon={<Link2 className="h-4 w-4" />} variant="dark">
        <div className="space-y-3 p-5">
          <div className="rounded-md border bg-muted/50 px-3 py-2">
            <code className="text-sm font-mono break-all">{uri || '—'}</code>
          </div>
        </div>
      </ProfileSection>
    </div>
  )
}
